/**
 * 股价走势节点视图组件
 * 用于渲染 Lexical PriceTrendNode
 */

import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import type { PriceTrendData } from "@/components/Editor/nodes/types";
import { cn } from "@/lib/utils";

interface PriceTrendViewProps {
  data: PriceTrendData;
  className?: string;
}

const trendConfig = {
  BULLISH: {
    label: "看涨",
    icon: TrendingUp,
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-100 dark:bg-green-900/30",
    border: "border-green-300 dark:border-green-700",
  },
  BEARISH: {
    label: "看跌",
    icon: TrendingDown,
    color: "text-red-600 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900/30",
    border: "border-red-300 dark:border-red-700",
  },
  NEUTRAL: {
    label: "中性",
    icon: Minus,
    color: "text-gray-600 dark:text-gray-400",
    bg: "bg-gray-100 dark:bg-gray-800",
    border: "border-gray-300 dark:border-gray-600",
  },
};

export const PriceTrendView: React.FC<PriceTrendViewProps> = ({ data, className }) => {
  const { symbol, trend, support, resistance, target, stopLoss, notes, chartUrl } = data;
  const config = trendConfig[trend];
  const TrendIcon = config.icon;

  return (
    <div className={cn("my-4 p-4 rounded-lg border bg-card", "price-trend-node", className)}>
      {/* 标题栏 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", config.bg, config.border)}>
            <TrendIcon className={cn("w-5 h-5", config.color)} />
          </div>
          <div>
            <div className="text-sm text-muted-foreground">股价走势分析</div>
            <div className="text-lg font-bold">{symbol}</div>
          </div>
        </div>
        <span className={cn("px-3 py-1 text-sm font-semibold rounded-md border", config.color, config.bg, config.border)}>
          {config.label}
        </span>
      </div>

      {/* 价格信息网格 */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* 支撑位 */}
        <div className="p-3 rounded-lg bg-secondary/20">
          <div className="text-sm text-muted-foreground mb-1">支撑位</div>
          <div className="text-lg font-bold text-green-600 dark:text-green-400">${support.toFixed(2)}</div>
        </div>

        {/* 阻力位 */}
        <div className="p-3 rounded-lg bg-secondary/20">
          <div className="text-sm text-muted-foreground mb-1">阻力位</div>
          <div className="text-lg font-bold text-red-600 dark:text-red-400">${resistance.toFixed(2)}</div>
        </div>

        {/* 目标价 */}
        <div className="p-3 rounded-lg bg-secondary/20">
          <div className="text-sm text-muted-foreground mb-1">目标价</div>
          <div className="text-lg font-bold text-primary">${target.toFixed(2)}</div>
        </div>

        {/* 止损价（可选） */}
        {stopLoss && (
          <div className="p-3 rounded-lg bg-secondary/20">
            <div className="text-sm text-muted-foreground mb-1">止损价</div>
            <div className="text-lg font-bold text-orange-600 dark:text-orange-400">${stopLoss.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* 价格区间可视化 */}
      <div className="mb-4 p-3 rounded-lg bg-muted/30">
        <div className="text-xs text-muted-foreground mb-2">价格区间</div>
        <div className="relative h-8">
          {/* 简单的价格区间可视化 */}
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-secondary -translate-y-1/2" />
          <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-2">
            <div className="text-xs font-medium text-green-600 dark:text-green-400">${support.toFixed(0)}</div>
            <div className="text-xs font-medium text-primary">${target.toFixed(0)}</div>
            <div className="text-xs font-medium text-red-600 dark:text-red-400">${resistance.toFixed(0)}</div>
          </div>
        </div>
      </div>

      {/* 图表（可选） */}
      {chartUrl && (
        <div className="mb-4">
          <img src={chartUrl} alt={`${symbol} 价格走势图`} className="w-full rounded-lg border" />
        </div>
      )}

      {/* 备注 */}
      {notes && (
        <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
          <div className="font-medium mb-1">分析备注:</div>
          <div>{notes}</div>
        </div>
      )}
    </div>
  );
};

export default PriceTrendView;
